/**
 * @file Xoe conversations CRUD API (healthcare practitioner/xoe endpoints).
 * @module api/xoe/XoeConversationsApi
 */

import { XoeConversation, XoeConversationDetail, XoePatientCandidate } from '../../types/xoe/XoeTypes';
import { ENV } from '../../config/runtimeEnv';
import type { UserUuid } from '../../types/identity';
import { http } from '../../lib/http/client';

// ── Request payload shapes ────────────────────────────────────────────────────

export interface ListConversationsParams {
	patient_id?: UserUuid;
	q?: string;
}

export interface CreateConversationPayload {
	title: string;
	patient_id?: UserUuid;
	expert_id?: string;
}

export interface GetConversationParams {
	uuid: string;
}

export interface UpdateConversationPayload {
	uuid: string;
	title?: string;
	archived_at?: string | null;
	/** expert_id to pin on the conversation; null reverts to Auto routing. */
	expert_id?: string | null;
	/** Bind the conversation to a patient (S6); null detaches it. */
	patient_id?: UserUuid | null;
}

export interface RemoveConversationParams {
	uuid: string;
}

export interface ResolveXoePatientParams {
	uuid: string;
	/** A patient name (or fragment) to search for within the practitioner's scope. */
	query: string;
}

// ── API functions ─────────────────────────────────────────────────────────────

/** List all conversations for the practitioner, optionally filtered by patient. */
export const listXoeConversations = async (
	params: ListConversationsParams,
): Promise<XoeConversation[]> => {
	const response = await http.get(
		`${ENV.VITE_XOE_AGENT_SERVER}/practitioner/xoe/conversations`,
		{
			params,
		},
	);
	// Backend wraps the list: { data: [...] }
	return response.data.data;
};

/** Create a new Xoe conversation. */
export const createXoeConversation = async (
	payload: CreateConversationPayload,
): Promise<XoeConversation> => {
	const response = await http.post(
		`${ENV.VITE_XOE_AGENT_SERVER}/practitioner/xoe/conversations`,
		payload,
	);
	// Backend wraps (201): { data: conversation }
	return response.data.data;
};

/**
 * Fetch a conversation with its messages via the dedicated poll endpoint.
 */
export const getXoeConversation = async (
	params: GetConversationParams,
): Promise<XoeConversationDetail> => {
	const { uuid } = params;
	const response = await http.get(
		`${ENV.VITE_XOE_AGENT_SERVER}/practitioner/xoe/conversations/${uuid}/messages`,
	);
	// Endpoint returns { data: <conversation>, messages: <paginator> }.
	// The conversation lives under `data` (NOT `conversation`) — map explicitly.
	return { conversation: response.data.data, messages: response.data.messages };
};

/** Update conversation title or archive state. */
export const updateXoeConversation = async (
	payload: UpdateConversationPayload,
): Promise<XoeConversation> => {
	const { uuid, ...body } = payload;
	const response = await http.patch(
		`${ENV.VITE_XOE_AGENT_SERVER}/practitioner/xoe/conversations/${uuid}`,
		body,
	);
	// Backend wraps: { data: conversation }
	return response.data.data;
};

/**
 * Resolve a patient by name within a conversation (S6). Returns 0..N candidates for
 * the in-chat patient picker — the caller binds the chosen one via updateXoeConversation.
 */
export const resolveXoePatient = async (
	params: ResolveXoePatientParams,
): Promise<XoePatientCandidate[]> => {
	const { uuid, query } = params;
	const response = await http.post(
		`${ENV.VITE_XOE_AGENT_SERVER}/practitioner/xoe/conversations/${uuid}/resolve-patient`,
		{ query },
	);
	// Backend wraps: { data: [...candidates], count }
	return response.data.data;
};

/** Delete a conversation. */
export const removeXoeConversation = async (
	params: RemoveConversationParams,
): Promise<void> => {
	const { uuid } = params;
	await http.delete(
		`${ENV.VITE_XOE_AGENT_SERVER}/practitioner/xoe/conversations/${uuid}`,
	);
};
